/**
 * Remembering who sent you here.
 *
 * A shared link carries ?ref=<member id>. If the person who follows it goes on
 * to do something real - back a cause, give their time - that sharing led to
 * it, and the ripple belongs to whoever shared.
 *
 * Three things worth knowing about how this is stored.
 *
 * It expires. A referral held forever would keep crediting somebody for a link
 * clicked last year, so it is stamped on arrival and ignored once it is older
 * than the attribution window the server enforces. Both ends agree on the
 * number, and the server is the one that counts.
 *
 * The first one wins. Someone browsing several shared links should not have
 * their eventual donation credited to whichever page they happened to see
 * last - the person who actually brought them here is the one who did the
 * work.
 *
 * It is a claim, not a credential. Anything can be put in a query string, so
 * the server treats it as partial evidence and still refuses self-crediting,
 * reciprocal pairs and anything outside the window. Forging one can at best
 * hand credit to a stranger.
 */

const KEY = 'tide.referral';

// Matches ATTRIBUTION_WINDOW_DAYS in backend/impact/ripples.py. The server is
// authoritative; this just avoids sending something certain to be refused.
const WINDOW_DAYS = 30;

/** Read ?ref= off the current URL and remember it. Safe to call on any page. */
export function captureReferral(search = window.location.search) {
  try {
    const ref = new URLSearchParams(search).get('ref');
    if (!ref) return null;

    const existing = getReferral();
    if (existing) return existing;      // first one wins

    const record = { ref, at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(record));
    return ref;
  } catch {
    // Private browsing can refuse localStorage. Losing a referral is a much
    // smaller problem than a page that will not load.
    return null;
  }
}

/** The remembered referrer, or null if there is none or it has gone stale. */
export function getReferral() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const { ref, at } = JSON.parse(raw);
    const ageDays = (Date.now() - at) / 86_400_000;
    if (!ref || ageDays > WINDOW_DAYS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return ref;
  } catch {
    return null;
  }
}

export function clearReferral() {
  try {
    localStorage.removeItem(KEY);
  } catch { /* nothing to do */ }
}

/** Build a shareable URL that credits the sharer if it leads anywhere. */
export function shareUrl(path, userId) {
  const url = new URL(path, window.location.origin);
  if (userId) url.searchParams.set('ref', userId);
  return url.toString();
}
